import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Sparkles, Send, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import { useLang } from "@/context/LanguageContext";

export default function Chat() {
  const { t } = useLang();
  const [sessionId, setSessionId] = useState(() => localStorage.getItem("mm_session") || null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  const SUGGESTIONS = [
    t("chat.sug1"),
    t("chat.sug2"),
    t("chat.sug3"),
    t("chat.sug4"),
  ];

  useEffect(() => {
    if (sessionId) {
      api.get(`/chat/${sessionId}/history`).then((r) => setMessages(r.data || [])).catch(() => {});
    }
  }, [sessionId]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading]);

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message || loading) return;
    setLoading(true);
    setMessages((m) => [...m, { role: "user", content: message }]);
    setInput("");
    try {
      const r = await api.post("/chat", { session_id: sessionId, message });
      if (!sessionId) {
        setSessionId(r.data.session_id);
        localStorage.setItem("mm_session", r.data.session_id);
      }
      setMessages((m) => [...m, { role: "assistant", content: r.data.reply }]);
    } catch (e) {
      toast.error(t("chat.err"));
      setMessages((m) => m.slice(0, -1));
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    localStorage.removeItem("mm_session");
    setSessionId(null);
    setMessages([]);
  };

  return (
    <div className="max-w-3xl mx-auto flex flex-col h-[calc(100vh-220px)]" data-testid="chat-page">
      <header className="mb-6 flex items-end justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-[0.25em] text-muted-foreground mb-2 font-mono">
            <span className="title-rule" />{t("chat.eyebrow")}
          </div>
          <h1 className="font-serif text-3xl sm:text-4xl font-medium tracking-tight">
            {t("chat.title")} <Sparkles className="inline size-6 text-accent ml-1 -mt-1" />
          </h1>
          <p className="text-sm text-muted-foreground mt-1.5">{t("chat.lead")}</p>
        </div>
        {messages.length > 0 && (
          <button onClick={reset} className="text-xs text-muted-foreground hover:text-destructive" data-testid="chat-reset-btn">
            {t("chat.reset")}
          </button>
        )}
      </header>

      <div ref={scrollRef} className="flex-1 overflow-y-auto pr-1 space-y-4" data-testid="chat-messages">
        {messages.length === 0 && (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground mb-2">{t("chat.suggestionsTitle")}</p>
            {SUGGESTIONS.map((s, i) => (
              <button
                key={i}
                onClick={() => send(s)}
                data-testid={`suggestion-${i}`}
                className="w-full text-left p-4 rounded-md border border-border bg-card hover:border-accent/50 hover:bg-card/60 transition-all text-sm"
              >
                {s}
              </button>
            ))}
          </div>
        )}

        {messages.map((m, i) => {
          const msgKey = m.id || `msg-${i}-${m.role}`;
          return (
          <div key={msgKey} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"} rise`}>
            <div
              data-testid={`msg-${m.role}-${i}`}
              className={`max-w-[85%] rounded-lg px-4 py-3 text-sm leading-relaxed ${
                m.role === "user"
                  ? "bg-primary text-primary-foreground whitespace-pre-wrap"
                  : "bg-card border border-border markdown-body"
              }`}
            >
              {m.role === "user" ? m.content : <ReactMarkdown>{m.content}</ReactMarkdown>}
            </div>
          </div>
          );
        })}

        {loading && (
          <div className="flex justify-start">
            <div className="bg-card border border-border rounded-lg px-4 py-3 text-sm text-muted-foreground flex items-center gap-2">
              <Loader2 className="size-4 animate-spin" /> {t("chat.thinking")}
            </div>
          </div>
        )}
      </div>

      <div className="mt-5 sticky bottom-0 bg-background pt-3">
        <form
          onSubmit={(e) => { e.preventDefault(); send(); }}
          className="flex items-end gap-2 bg-card border border-border rounded-lg p-2 focus-within:border-primary/50 transition-colors"
        >
          <Textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
            }}
            placeholder={t("chat.input")}
            data-testid="chat-input"
            className="resize-none border-0 focus-visible:ring-0 min-h-[44px] max-h-32 bg-transparent"
            rows={1}
          />
          <Button type="submit" size="icon" disabled={loading || !input.trim()} data-testid="chat-send-btn">
            <Send className="size-4" />
          </Button>
        </form>
        <p className="text-[11px] text-muted-foreground mt-2 px-1">
          {t("chat.disclaimer")}
        </p>
      </div>
    </div>
  );
}
